import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import useSchool, { BRAND_COLOR_FALLBACK } from '@/hooks/useSchool';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2, BarChart3, Sparkles, Info } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
} from 'recharts';
import RemedialAssessmentDialog from '@/components/RemedialAssessmentDialog';

const STATUS_STYLES = {
  strong:  { dot: 'bg-emerald-500', row: 'border-emerald-200 bg-emerald-50/40', label: 'Strong' },
  mid:     { dot: 'bg-amber-500',   row: 'border-amber-200 bg-amber-50/40',     label: 'Mid' },
  lagging: { dot: 'bg-rose-500',    row: 'border-rose-200 bg-rose-50/50',       label: 'Lagging' },
  no_data: { dot: 'bg-slate-300',   row: 'border-slate-200 bg-slate-50',        label: 'No data' },
};

/**
 * Class-level analytics for one assessment. Aggregates only — no per-student names.
 * Surfaced via the "Analytics" button on the per-student status page.
 */
export default function SchoolTeacherAssessmentAnalytics() {
  const navigate = useNavigate();
  const { aid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  // CUM-4 / Phase 5-C — remedial assessment dialog state. Must live at the
  // top of the component (React Hook rules) — referenced in JSX at the bottom.
  const [remedialOpen, setRemedialOpen] = useState(false);
  const [remedialTopic, setRemedialTopic] = useState(null);
  const school = useSchool(me?.school_id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/teacher/me/assessments/${aid}/analytics`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load analytics');
      navigate(`/school-teacher/assessments/${aid}/status`);
    } finally {
      setLoading(false);
    }
  }, [aid, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get('/auth/me');
        setMe(meRes.data);
      } catch { /* */ }
    })();
    load();
  }, [load]);

  const brandColor = school?.brand_color || BRAND_COLOR_FALLBACK;

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-indigo-600" />
      </div>
    );
  }

  const { assessment, kpis, score_buckets, top_missed_questions, duration } = data;
  const reasonDist = data.reason_score_distribution || [];
  const topicBreakdown = data.topic_breakdown || [];

  const openRemedial = (tag) => {
    setRemedialTopic(tag);
    setRemedialOpen(true);
  };

  const kpiCards = [
    { label: 'Total students', value: kpis.total_students },
    { label: 'Submitted', value: kpis.submitted },
    { label: 'Released', value: kpis.released },
    { label: 'Avg score', value: kpis.avg_score_pct == null ? '—' : `${kpis.avg_score_pct}%` },
  ];

  const noSubmissions = kpis.submitted === 0;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 pb-12"
      data-testid="school-teacher-assessment-analytics"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/school-teacher/assessments/${aid}/status`)}
            data-testid="back-btn"
          >
            <ArrowLeft size={16} className="mr-1.5" />
            Back to status
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-slate-900" data-testid="analytics-title">
              {assessment?.title || 'Assessment analytics'}
            </h1>
            <p className="text-xs text-slate-500">Class-level performance · aggregates only</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* KPI tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="analytics-kpis">
          {kpiCards.map((c) => (
            <div
              key={c.label}
              className="bg-white border rounded-xl p-4 text-center"
              data-testid={`kpi-${c.label.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="text-2xl font-bold text-slate-900">{c.value}</div>
              <div className="text-xs text-slate-500 mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        {/* Secondary metrics row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <SecondaryStat
            label="Started"
            value={`${kpis.started} / ${kpis.total_students}`}
            data-testid="stat-started"
          />
          <SecondaryStat
            label="Median time taken"
            value={kpis.median_duration_minutes == null ? '—' : `${kpis.median_duration_minutes} min`}
            data-testid="stat-median-duration"
          />
          <SecondaryStat
            label="Overdue (after due date)"
            value={kpis.overdue_count}
            data-testid="stat-overdue"
          />
        </div>

        {noSubmissions ? (
          <div
            className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-10 text-center"
            data-testid="analytics-empty"
          >
            <BarChart3 size={28} className="mx-auto text-slate-400 mb-2" />
            <h2 className="text-base font-semibold text-slate-700 mb-1">
              No submissions yet
            </h2>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Charts will appear here once at least one student submits this assessment.
            </p>
          </div>
        ) : (
          <>
            {/* Score distribution */}
            <section
              className="bg-white border rounded-xl p-5"
              data-testid="score-distribution-card"
            >
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                Score distribution
              </h3>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={score_buckets} margin={{ top: 8, right: 8, left: -12, bottom: 4 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                    <Bar dataKey="count" fill={brandColor} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Min {duration.min_minutes ?? '—'} · Median {duration.median_minutes ?? '—'} · Max {duration.max_minutes ?? '—'} min
              </p>
            </section>

            {/* Top missed questions */}
            <section
              className="bg-white border rounded-xl p-5"
              data-testid="top-missed-card"
            >
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                Top missed questions
              </h3>
              {top_missed_questions.length === 0 ? (
                <p className="text-sm text-slate-500">Everyone got every question right ✨</p>
              ) : (
                <ul className="space-y-3" data-testid="top-missed-list">
                  {top_missed_questions.map((q, idx) => (
                    <li
                      key={q.question_id}
                      className="flex items-start gap-3"
                      data-testid={`top-missed-${q.question_id}`}
                    >
                      <span className="font-mono text-slate-400 text-xs mt-1 w-5">{idx + 1}.</span>
                      <div className="flex-1">
                        <div className="text-sm text-slate-800">{q.prompt_snippet}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {q.incorrect_pct}% incorrect · most common wrong answer:{' '}
                          <span className="font-mono font-semibold text-rose-600">{q.common_wrong_option}</span>
                          {' · '}{q.attempted} attempt{q.attempted === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div className="w-24 sm:w-32">
                        <div
                          className="h-2 rounded-full bg-slate-100 overflow-hidden"
                          aria-label={`${q.incorrect_pct}% incorrect`}
                        >
                          <div
                            className="h-full"
                            style={{
                              width: `${q.incorrect_pct}%`,
                              backgroundColor: '#F43F5E',
                            }}
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Topic Breakdown (Phase 5-C.2) */}
            {topicBreakdown.length > 0 && (
              <section
                className="bg-white border rounded-xl p-5"
                data-testid="topic-breakdown-card"
              >
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Topic breakdown
                </h3>
                <ul className="space-y-2" data-testid="topic-breakdown-list">
                  {topicBreakdown.map((t) => {
                    const style = STATUS_STYLES[t.status] || STATUS_STYLES.mid;
                    return (
                      <li
                        key={t.concept_tag}
                        className={`flex flex-col sm:flex-row sm:items-center gap-3 border rounded-lg p-3 ${style.row}`}
                        data-testid={`topic-row-${t.concept_tag.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className={`inline-block w-2.5 h-2.5 rounded-full ${style.dot}`} />
                          <span className="font-medium text-slate-900 truncate" title={t.concept_tag}>
                            {t.concept_tag}
                          </span>
                          <Badge variant="outline" className="text-[10px] bg-white">
                            {t.question_count} Q{t.question_count === 1 ? '' : 's'}
                          </Badge>
                          {t.sparse_data && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[10px] text-slate-500"
                              title="Fewer than 5 submissions on this topic — signal is noisy"
                              data-testid={`topic-sparse-${t.concept_tag.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              <Info size={10} /> sparse
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="text-right min-w-[88px]">
                            <div className="text-slate-900 font-semibold">
                              {t.correct_pct == null ? '—' : `${t.correct_pct}%`}
                            </div>
                            <div className="text-[10px] text-slate-500 uppercase">Correct</div>
                          </div>
                          <div className="text-right min-w-[68px]">
                            <div className="text-slate-900 font-semibold">
                              {t.reasoning_avg == null ? '—' : `${t.reasoning_avg.toFixed(1)}/5`}
                            </div>
                            <div className="text-[10px] text-slate-500 uppercase">Reasoning</div>
                          </div>
                          {t.status === 'lagging' && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => openRemedial(t.concept_tag)}
                              data-testid={`remedial-cta-${t.concept_tag.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              <Sparkles size={14} className="mr-1" /> Generate remedial…
                            </Button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <p className="text-[11px] text-slate-400 mt-3">
                  🔴 Lagging: under 50% correct or reasoning below 2.5/5  ·  🟡 Mid  ·  🟢 Strong: 75%+ correct &amp; reasoning ≥ 3.5/5
                </p>
              </section>
            )}

            {/* Reason-quality distribution (PR 5-B will populate; hidden until available) */}
            {reasonDist.length > 0 && (
              <section
                className="bg-white border rounded-xl p-5"
                data-testid="reason-score-card"
              >
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Reasoning quality (0 – 5)
                </h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reasonDist} margin={{ top: 8, right: 8, left: -12, bottom: 4 }}>
                      <XAxis dataKey="score" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {reasonDist.map((d) => (
                          <Cell key={d.score} fill={brandColor} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <RemedialAssessmentDialog
        open={remedialOpen}
        onOpenChange={setRemedialOpen}
        sourceAid={aid}
        sourceTitle={assessment?.title || ''}
        conceptTag={remedialTopic || ''}
        defaultDurationMinutes={assessment?.duration_minutes || 20}
        onCreated={(newAid) => {
          navigate(`/school-teacher/assessments/${newAid}`);
        }}
      />
    </div>
  );
}

function SecondaryStat({ label, value, ...rest }) {
  return (
    <div
      className="bg-white border rounded-xl p-4 flex items-center justify-between"
      {...rest}
    >
      <span className="text-slate-500 text-xs uppercase tracking-wide">{label}</span>
      <span className="font-semibold text-slate-900">{value}</span>
    </div>
  );
}
